import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ThemeProvider,
  createTheme,
  Box,
  Typography,
  IconButton,
  Paper,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  FormControl,
  Select,
  MenuItem,
  Skeleton,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  School as SchoolIcon,
  Search as SearchIcon,
  VisibilityOutlined as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as PendingIcon,
  UploadFile as UploadFileIcon,
  WorkspacePremium as CertificateIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import axios from "axios";
import { BASE_URL } from "../../../services/configUrls";

// Custom theme matching AttendanceSystem
const theme = createTheme({
  typography: {
    fontFamily: "Poppins",
    fontSize: 12,
    body1: { fontSize: "0.75rem" },
    body2: { fontSize: "0.7rem" },
    h6: { fontSize: "0.9rem" },
    h4: { fontSize: "1.5rem" },
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "8px 12px",
          fontSize: "0.75rem",
        },
        head: {
          fontSize: "0.75rem",
          fontWeight: 600,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: "Poppins",
          textTransform: "none",
        },
      },
    },
  },
});

// Helper for formatting date strings
const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return String(dateStr);
  }
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return "—";
  const formatSingle = (str) => {
    try {
      const d = new Date(str);
      return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      return str;
    }
  };
  return `${formatSingle(startDate)} - ${formatSingle(endDate)}`;
};

// Helper for overall status badge
const getOverallStatusBadge = (status) => {
  const norm = (status || "").toLowerCase();
  switch (norm) {
    case "verified":
      return {
        label: "Verified",
        color: "#166534",
        bgcolor: "#DCFCE7",
        border: "#BBF7D0",
      };
    case "uploaded":
      return {
        label: "Uploaded",
        color: "#1E40AF",
        bgcolor: "#DBEAFE",
        border: "#BFDBFE",
      };
    case "partially_uploaded":
      return {
        label: "Partially Uploaded",
        color: "#92400E",
        bgcolor: "#FEF3C7",
        border: "#FDE68A",
      };
    case "rejected":
      return {
        label: "Rejected",
        color: "#991B1B",
        bgcolor: "#FEE2E2",
        border: "#FECACA",
      };
    case "not_uploaded":
      return {
        label: "Not Uploaded",
        color: "#475569",
        bgcolor: "#F1F5F9",
        border: "#E2E8F0",
      };
    case "not_required":
      return {
        label: "Not Required",
        color: "#64748B",
        bgcolor: "#F8FAFC",
        border: "#E2E8F0",
      };
    default:
      return {
        label: status || "N/A",
        color: "#475569",
        bgcolor: "#F1F5F9",
        border: "#E2E8F0",
      };
  }
};

// Helper for individual certificate status badge
const getCertificateStatusBadge = (statusName, statusNum) => {
  const norm = (statusName || "").toLowerCase();
  const num = String(statusNum);

  if (norm === "verified" || num === "2") {
    return {
      label: "Verified",
      color: "#166534",
      bgcolor: "#DCFCE7",
      border: "#BBF7D0",
      icon: <CheckCircleIcon sx={{ fontSize: 13, color: "#16A34A" }} />,
    };
  }
  if (norm === "uploaded" || num === "1") {
    return {
      label: "Uploaded",
      color: "#1E40AF",
      bgcolor: "#DBEAFE",
      border: "#BFDBFE",
      icon: <UploadFileIcon sx={{ fontSize: 13, color: "#2563EB" }} />,
    };
  }
  if (norm === "rejected" || num === "3") {
    return {
      label: "Rejected",
      color: "#991B1B",
      bgcolor: "#FEE2E2",
      border: "#FECACA",
      icon: <CancelIcon sx={{ fontSize: 13, color: "#DC2626" }} />,
    };
  }
  return {
    label: "Not Uploaded",
    color: "#64748B",
    bgcolor: "#F1F5F9",
    border: "#E2E8F0",
    icon: <PendingIcon sx={{ fontSize: 13, color: "#94A3B8" }} />,
  };
};

// Helper for eligibility status badge
const getEligibilityBadge = (status) => {
  const norm = (status || "").toLowerCase();
  switch (norm) {
    case "passed":
      return {
        label: "Passed",
        color: "#166534",
        bgcolor: "#DCFCE7",
      };
    case "dropout":
      return {
        label: "Dropout",
        color: "#991B1B",
        bgcolor: "#FEE2E2",
      };
    default:
      return {
        label: "Not Marked",
        color: "#475569",
        bgcolor: "#F1F5F9",
      };
  }
};

// Helper for download status badge
const getDownloadBadge = (status) => {
  const norm = (status || "").toLowerCase();
  if (norm === "downloaded") {
    return {
      label: "Downloaded",
      color: "#1E40AF",
      bgcolor: "#DBEAFE",
    };
  }
  return {
    label: "Not Downloaded",
    color: "#64748B",
    bgcolor: "#F1F5F9",
  };
};

// ==========================================
// SUB-MODAL: Student Certificates Pop-up
// ==========================================
function StudentCertificatesDialog({ open, onClose, student }) {
  if (!student) return null;

  const certificates = student.certificates || [];
  const verifiedCount = certificates.filter(
    (c) => String(c.status) === "2" || (c.status_name || "").toLowerCase() === "verified"
  ).length;
  const rejectedCount = certificates.filter(
    (c) => String(c.status) === "3" || (c.status_name || "").toLowerCase() === "rejected"
  ).length;
  const uploadedCount = certificates.filter(
    (c) => String(c.status) === "1" || (c.status_name || "").toLowerCase() === "uploaded"
  ).length;
  const notUploadedCount = certificates.filter(
    (c) => String(c.status) === "0" || (c.status_name || "").toLowerCase() === "not_uploaded"
  ).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          fontFamily: "'Poppins', sans-serif",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: "#F8FAFC",
          borderBottom: "1px solid #E2E8F0",
          p: 2.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "10px",
              bgcolor: "#EEF2FF",
              color: "#4F46E5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CertificateIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: "16px",
                color: "#1E293B",
                fontFamily: "'Poppins', sans-serif",
                lineHeight: 1.2,
              }}
            >
              {student.name || "Student Certificates"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#64748B",
                fontSize: "12px",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {student.email || "No email"} • Roll No: {student.roll_no || "—"} • Branch: {student.branch || "—"}
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#64748B",
            bgcolor: "#FFFFFF",
            border: "1px solid #E2E8F0",
            "&:hover": { bgcolor: "#F1F5F9", color: "#0F172A" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Summary Chips */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            mb: 2.5,
            p: 1.5,
            borderRadius: "10px",
            bgcolor: "#F8FAFC",
            border: "1px solid #E2E8F0",
          }}
        >
          <Chip
            size="small"
            label={`Total Courses: ${certificates.length}`}
            sx={{
              fontWeight: 600,
              fontSize: "12px",
              bgcolor: "#FFFFFF",
              border: "1px solid #CBD5E1",
              fontFamily: "'Poppins', sans-serif",
            }}
          />
          <Chip
            size="small"
            label={`Verified: ${verifiedCount}`}
            sx={{
              fontWeight: 600,
              fontSize: "12px",
              bgcolor: "#DCFCE7",
              color: "#166534",
              border: "1px solid #BBF7D0",
              fontFamily: "'Poppins', sans-serif",
            }}
          />
          {uploadedCount > 0 && (
            <Chip
              size="small"
              label={`Uploaded: ${uploadedCount}`}
              sx={{
                fontWeight: 600,
                fontSize: "12px",
                bgcolor: "#DBEAFE",
                color: "#1E40AF",
                border: "1px solid #BFDBFE",
                fontFamily: "'Poppins', sans-serif",
              }}
            />
          )}
          {rejectedCount > 0 && (
            <Chip
              size="small"
              label={`Rejected: ${rejectedCount}`}
              sx={{
                fontWeight: 600,
                fontSize: "12px",
                bgcolor: "#FEE2E2",
                color: "#991B1B",
                border: "1px solid #FECACA",
                fontFamily: "'Poppins', sans-serif",
              }}
            />
          )}
          <Chip
            size="small"
            label={`Not Uploaded: ${notUploadedCount}`}
            sx={{
              fontWeight: 600,
              fontSize: "12px",
              bgcolor: "#F1F5F9",
              color: "#475569",
              border: "1px solid #E2E8F0",
              fontFamily: "'Poppins', sans-serif",
            }}
          />
        </Box>

        {/* Certificates Table */}
        {certificates.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center", color: "#64748B" }}>
            <Typography sx={{ fontSize: "14px", fontFamily: "'Poppins', sans-serif" }}>
              No course certificates mapped for this domain.
            </Typography>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: "1px solid #E2E8F0",
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: "12px", color: "#475569", py: 1.5, width: 50 }}>
                    #
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "12px", color: "#475569", py: 1.5 }}>
                    Course Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "12px", color: "#475569", py: 1.5, width: 140 }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "12px", color: "#475569", py: 1.5, width: 160 }}>
                    Verification Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "12px", color: "#475569", py: 1.5 }}>
                    Reason / Remarks
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {certificates.map((cert, idx) => {
                  const badge = getCertificateStatusBadge(cert.status_name, cert.status);
                  const isRejected = String(cert.status) === "3" || (cert.status_name || "").toLowerCase() === "rejected";

                  return (
                    <TableRow
                      key={cert.course_id || idx}
                      sx={{
                        "&:hover": { bgcolor: "#F8FAFC" },
                        bgcolor: isRejected ? "#FEF2F2" : "inherit",
                      }}
                    >
                      <TableCell sx={{ fontSize: "12.5px", color: "#64748B", fontFamily: "'Poppins', sans-serif" }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#1E293B", fontFamily: "'Poppins', sans-serif" }}>
                        {cert.course_name || `Course ID: ${cert.course_id}`}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={badge.icon}
                          label={badge.label}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "11.5px",
                            bgcolor: badge.bgcolor,
                            color: badge.color,
                            border: `1px solid ${badge.border}`,
                            fontFamily: "'Poppins', sans-serif",
                            height: 24,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px", color: "#64748B", fontFamily: "'Poppins', sans-serif" }}>
                        {cert.verify_date ? formatDateTime(cert.verify_date) : "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px", color: isRejected ? "#DC2626" : "#64748B", fontFamily: "'Poppins', sans-serif" }}>
                        {cert.reason ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {isRejected && <InfoIcon sx={{ fontSize: 14, color: "#DC2626" }} />}
                            <span>{cert.reason}</span>
                          </Box>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: "#F8FAFC", borderTop: "1px solid #E2E8F0" }}>
        <Button
          onClick={onClose}
          variant="contained"
          size="small"
          sx={{
            textTransform: "none",
            bgcolor: "#4F46E5",
            fontWeight: 600,
            borderRadius: "8px",
            px: 2.5,
            fontFamily: "'Poppins', sans-serif",
            "&:hover": { bgcolor: "#4338CA" },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ==========================================
// MAIN PAGE COMPONENT: TechCampStudentsView
// ==========================================
export default function TechCampStudentsView() {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [studentsData, setStudentsData] = useState(null);
  const [eventDetails, setEventDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters & search
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [eligibilityFilter, setEligibilityFilter] = useState("all");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Certificate pop-up modal state
  const [selectedStudentForCerts, setSelectedStudentForCerts] = useState(null);

  useEffect(() => {
    if (bookingId) {
      fetchAllData();
    }
  }, [bookingId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("accessToken");
      const headers = {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      };

      // Concurrent fetch: view-students + event trainer details
      const studentsPromise = (async () => {
        try {
          const res = await axios.get(`${BASE_URL}/event/view-students/${bookingId}`, { headers });
          return res.data;
        } catch (err) {
          if (err?.response?.status === 404) {
            const fallback = await axios.get(`${BASE_URL}/view-students/${bookingId}`, { headers });
            return fallback.data;
          }
          throw err;
        }
      })();

      const eventPromise = (async () => {
        try {
          const res = await axios.get(`${BASE_URL}/event/trainer/${bookingId}`, { headers });
          return res.data?.event || null;
        } catch (err) {
          return null;
        }
      })();

      const [studentsRes, eventRes] = await Promise.all([
        studentsPromise,
        eventPromise,
      ]);

      setStudentsData(studentsRes);
      if (eventRes) {
        setEventDetails(eventRes);
      }
    } catch (err) {
      console.error("Error fetching tech camp students:", err);
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to load students data. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToDashboard = () => {
    window.history.back();
  };

  const rawStudents = studentsData?.students || [];

  // Filter students
  const filteredStudents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    return rawStudents.filter((student) => {
      const nameMatch = (student.name || "").toLowerCase().includes(q);
      const emailMatch = (student.email || "").toLowerCase().includes(q);
      const rollMatch = (student.roll_no || "").toLowerCase().includes(q);
      const branchMatch = (student.branch || "").toLowerCase().includes(q);
      const aparMatch = (student.apar_id || "").toLowerCase().includes(q);
      const matchesSearch = !q || nameMatch || emailMatch || rollMatch || branchMatch || aparMatch;

      const overallNorm = (student.overall_status || "").toLowerCase();
      const matchesStatus = statusFilter === "all" || overallNorm === statusFilter.toLowerCase();

      const eligNorm = (student.eligible_status || "").toLowerCase();
      const matchesElig = eligibilityFilter === "all" || eligNorm === eligibilityFilter.toLowerCase();

      return matchesSearch && matchesStatus && matchesElig;
    });
  }, [rawStudents, searchTerm, statusFilter, eligibilityFilter]);

  // Paginated students
  const paginatedStudents = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredStudents.slice(start, start + rowsPerPage);
  }, [filteredStudents, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Metrics summary
  const metrics = useMemo(() => {
    const total = rawStudents.length;
    let verified = 0;
    let uploaded = 0;
    let notUploaded = 0;
    let rejected = 0;
    let passed = 0;

    rawStudents.forEach((s) => {
      const st = (s.overall_status || "").toLowerCase();
      if (st === "verified") verified++;
      else if (st === "uploaded" || st === "partially_uploaded") uploaded++;
      else if (st === "rejected") rejected++;
      else if (st === "not_uploaded") notUploaded++;

      if ((s.eligible_status || "").toLowerCase() === "passed") passed++;
    });

    return { total, verified, uploaded, notUploaded, rejected, passed };
  }, [rawStudents]);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            p: 2.5,
            maxWidth: 1800,
            mx: "auto",
            backgroundColor: "#FAFBFC",
            minHeight: "100vh",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="text" width={240} height={32} />
          </Box>
          <Paper sx={{ p: 2, mb: 2.5, boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>
            <Skeleton variant="text" width={140} height={20} sx={{ mb: 1 }} />
            <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              <Skeleton variant="text" width={220} height={16} />
              <Skeleton variant="text" width={180} height={16} />
              <Skeleton variant="text" width={120} height={16} />
            </Box>
          </Paper>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 1.5, mb: 2.5 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rounded" height={60} />
            ))}
          </Box>
          <Skeleton variant="rounded" height={350} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          p: 2.5,
          maxWidth: 1800,
          mx: "auto",
          backgroundColor: "#FAFBFC",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header Bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={handleBackToDashboard}
              sx={{
                backgroundColor: "#FFFFFF",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                border: "1px solid #E2E8F0",
                "&:hover": {
                  backgroundColor: "#F8FAFC",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
              size="small"
            >
              <ArrowBackIcon sx={{ color: "#3F51B5", fontSize: "1.1rem" }} />
            </IconButton>
            <SchoolIcon sx={{ fontSize: 26, color: "#3F51B5", mr: 0.5 }} />
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", color: "#1E293B", fontFamily: "'Poppins', sans-serif" }}
            >
              Tech Camp Students
            </Typography>
            <Chip
              label={`Total: ${studentsData?.total_students ?? rawStudents.length}`}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: "12px",
                bgcolor: "#4F46E5",
                color: "white",
                fontFamily: "'Poppins', sans-serif",
                height: 24,
                ml: 1,
              }}
            />
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={fetchAllData}
            sx={{
              borderColor: "#CBD5E1",
              color: "#475569",
              bgcolor: "#FFFFFF",
              fontWeight: 600,
              fontSize: "12.5px",
              borderRadius: "8px",
              "&:hover": { bgcolor: "#F8FAFC", borderColor: "#94A3B8" },
            }}
          >
            Refresh Data
          </Button>
        </Box>

        {/* Event Details Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2.5,
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
            bgcolor: "#FFFFFF",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 1, color: "#0F172A", fontWeight: 700, fontSize: "14.5px" }}
          >
            Event Information
          </Typography>
          <Box sx={{ display: "flex", gap: 3.5, flexWrap: "wrap", alignItems: "center" }}>
            <Typography variant="body2" sx={{ color: "#475569" }}>
              <strong>Institute:</strong> {eventDetails?.institute_name || "—"}
            </Typography>
            {eventDetails?.start_date && eventDetails?.end_date && (
              <Typography variant="body2" sx={{ color: "#475569" }}>
                <strong>Duration:</strong>{" "}
                {formatDateRange(eventDetails.start_date, eventDetails.end_date)}
              </Typography>
            )}
            {eventDetails?.trainer_name && (
              <Typography variant="body2" sx={{ color: "#475569" }}>
                <strong>Trainer:</strong> {eventDetails.trainer_name}
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: "#475569" }}>
              <strong>Slot ID:</strong> #{bookingId}
            </Typography>
            {studentsData?.domain_id && (
              <Typography variant="body2" sx={{ color: "#475569" }}>
                <strong>Domain ID:</strong> #{studentsData.domain_id}
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Summary Metric Cards */}
        {rawStudents.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
                md: "repeat(5, 1fr)",
              },
              gap: 1.5,
              mb: 2.5,
              flexShrink: 0,
            }}
          >
            <Card
              variant="outlined"
              sx={{ borderRadius: "10px", borderColor: "#E2E8F0", bgcolor: "#FFFFFF" }}
            >
              <CardContent sx={{ p: "12px 16px !important" }}>
                <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600 }}>
                  TOTAL ENROLLED
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#1E293B", mt: 0.3 }}>
                  {metrics.total}
                </Typography>
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              sx={{ borderRadius: "10px", borderColor: "#BBF7D0", bgcolor: "#F0FDF4" }}
            >
              <CardContent sx={{ p: "12px 16px !important" }}>
                <Typography variant="caption" sx={{ color: "#166534", fontWeight: 600 }}>
                  ALL VERIFIED
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#16A34A", mt: 0.3 }}>
                  {metrics.verified}
                </Typography>
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              sx={{ borderRadius: "10px", borderColor: "#BFDBFE", bgcolor: "#EFF6FF" }}
            >
              <CardContent sx={{ p: "12px 16px !important" }}>
                <Typography variant="caption" sx={{ color: "#1E40AF", fontWeight: 600 }}>
                  UPLOADED
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#2563EB", mt: 0.3 }}>
                  {metrics.uploaded}
                </Typography>
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              sx={{ borderRadius: "10px", borderColor: "#E2E8F0", bgcolor: "#FFFFFF" }}
            >
              <CardContent sx={{ p: "12px 16px !important" }}>
                <Typography variant="caption" sx={{ color: "#475569", fontWeight: 600 }}>
                  NOT UPLOADED
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#475569", mt: 0.3 }}>
                  {metrics.notUploaded}
                </Typography>
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              sx={{ borderRadius: "10px", borderColor: "#FECACA", bgcolor: "#FEF2F2" }}
            >
              <CardContent sx={{ p: "12px 16px !important" }}>
                <Typography variant="caption" sx={{ color: "#991B1B", fontWeight: 600 }}>
                  HAS REJECTIONS
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#DC2626", mt: 0.3 }}>
                  {metrics.rejected}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Toolbar & Filter Card */}
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2.5,
            border: "1px solid #E2E8F0",
            borderRadius: "10px",
            bgcolor: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <TextField
            size="small"
            placeholder="Search students by name, email, roll no, branch, apar id..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94A3B8", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flexGrow: 1,
              minWidth: { xs: "100%", sm: 300 },
              "& .MuiOutlinedInput-root": {
                height: 38,
                borderRadius: "8px",
                fontSize: "13px",
                fontFamily: "'Poppins', sans-serif",
              },
            }}
          />

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", width: { xs: "100%", sm: "auto" } }}>
            {/* Status Filter */}
            <FormControl size="small" sx={{ minWidth: 180, flex: { xs: 1, sm: "none" } }}>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                sx={{
                  height: 38,
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="uploaded">Uploaded</MenuItem>
                <MenuItem value="partially_uploaded">Partially Uploaded</MenuItem>
                <MenuItem value="not_uploaded">Not Uploaded</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>

            {/* Eligibility Filter */}
            <FormControl size="small" sx={{ minWidth: 150, flex: { xs: 1, sm: "none" } }}>
              <Select
                value={eligibilityFilter}
                onChange={(e) => {
                  setEligibilityFilter(e.target.value);
                  setPage(0);
                }}
                sx={{
                  height: 38,
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="passed">Passed</MenuItem>
                <MenuItem value="dropout">Dropout</MenuItem>
                <MenuItem value="not_marked">Not Marked</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: "8px" }}
            action={
              <Button color="inherit" size="small" onClick={fetchAllData}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Main Students Table */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
            flexGrow: 1,
            overflowX: "auto",
            bgcolor: "#FFFFFF",
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    bgcolor: "#F8FAFC",
                    fontWeight: 700,
                    fontSize: "12px",
                    color: "#475569",
                    py: 1.5,
                  },
                }}
              >
                <TableCell align="center" sx={{ width: 45 }}>
                  #
                </TableCell>
                <TableCell>Student Details</TableCell>
                <TableCell>Roll No</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>APAR ID</TableCell>
                <TableCell>Passing Year</TableCell>
                <TableCell align="center">Eligibility</TableCell>
                <TableCell align="center">Download</TableCell>
                <TableCell align="center">Overall Status</TableCell>
                <TableCell align="center" sx={{ width: 140 }}>
                  Certificates
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedStudents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8, color: "#64748B" }}>
                    <Typography sx={{ fontSize: "14px", fontFamily: "'Poppins', sans-serif" }}>
                      No students found matching your criteria.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedStudents.map((student, idx) => {
                  const overallBadge = getOverallStatusBadge(student.overall_status);
                  const eligBadge = getEligibilityBadge(student.eligible_status);
                  const dlBadge = getDownloadBadge(student.download_status);
                  const certCount = student.certificates?.length || 0;

                  return (
                    <TableRow
                      key={student.user_id || idx}
                      hover
                      sx={{ "&:hover": { bgcolor: "#F8FAFC" } }}
                    >
                      {/* Row Number */}
                      <TableCell align="center" sx={{ fontSize: "12.5px", color: "#64748B" }}>
                        {page * rowsPerPage + idx + 1}
                      </TableCell>

                      {/* Student Details */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "13.5px",
                              color: "#1E293B",
                              fontFamily: "'Poppins', sans-serif",
                              lineHeight: 1.25,
                            }}
                          >
                            {student.name || "—"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#64748B",
                              fontSize: "12px",
                              fontFamily: "'Poppins', sans-serif",
                              wordBreak: "break-all",
                            }}
                          >
                            {student.email || "—"}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Roll No */}
                      <TableCell sx={{ fontSize: "12.5px", color: "#334155" }}>
                        {student.roll_no || "—"}
                      </TableCell>

                      {/* Branch */}
                      <TableCell sx={{ fontSize: "12.5px", color: "#334155" }}>
                        {student.branch || "—"}
                      </TableCell>

                      {/* APAR ID */}
                      <TableCell sx={{ fontSize: "12.5px", color: "#334155" }}>
                        {student.apar_id || "—"}
                      </TableCell>

                      {/* Final Year */}
                      <TableCell sx={{ fontSize: "12.5px", color: "#334155" }}>
                        {student.final_year || "—"}
                      </TableCell>

                      {/* Eligibility */}
                      <TableCell align="center">
                        <Chip
                          label={eligBadge.label}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "11px",
                            bgcolor: eligBadge.bgcolor,
                            color: eligBadge.color,
                            height: 22,
                            fontFamily: "'Poppins', sans-serif",
                          }}
                        />
                      </TableCell>

                      {/* Download Status */}
                      <TableCell align="center">
                        <Chip
                          label={dlBadge.label}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "11px",
                            bgcolor: dlBadge.bgcolor,
                            color: dlBadge.color,
                            height: 22,
                            fontFamily: "'Poppins', sans-serif",
                          }}
                        />
                      </TableCell>

                      {/* Overall Certificate Status */}
                      <TableCell align="center">
                        <Chip
                          label={overallBadge.label}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "11.5px",
                            bgcolor: overallBadge.bgcolor,
                            color: overallBadge.color,
                            border: `1px solid ${overallBadge.border}`,
                            height: 24,
                            fontFamily: "'Poppins', sans-serif",
                          }}
                        />
                      </TableCell>

                      {/* Certificates View Action */}
                      <TableCell align="center">
                        <Tooltip title="View Certificate Details">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                            onClick={() => setSelectedStudentForCerts(student)}
                            sx={{
                              textTransform: "none",
                              fontSize: "12px",
                              fontWeight: 600,
                              borderColor: "#CBD5E1",
                              color: "#4F46E5",
                              borderRadius: "6px",
                              py: 0.3,
                              px: 1.2,
                              fontFamily: "'Poppins', sans-serif",
                              "&:hover": {
                                borderColor: "#4F46E5",
                                bgcolor: "#EEF2FF",
                              },
                            }}
                          >
                            {certCount} View
                          </Button>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {filteredStudents.length > 0 && (
          <TablePagination
            component="div"
            count={filteredStudents.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              mt: 1,
              borderTop: "1px solid #E2E8F0",
              "& .MuiTablePagination-toolbar": { minHeight: 48 },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "12.5px",
                fontFamily: "'Poppins', sans-serif",
              },
            }}
          />
        )}

        {/* Footer */}
        <Box
          sx={{
            mt: 4,
            py: 2,
            textAlign: "center",
            borderTop: "1px solid #E5E7EB",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "#6B7280", fontSize: "0.7rem", fontFamily: "'Poppins', sans-serif" }}
          >
            © {new Date().getFullYear()} Eduskills Foundation. All rights reserved.
          </Typography>
        </Box>
      </Box>

      {/* Pop-up Dialog for Individual Student Certificates */}
      <StudentCertificatesDialog
        open={Boolean(selectedStudentForCerts)}
        onClose={() => setSelectedStudentForCerts(null)}
        student={selectedStudentForCerts}
      />
    </ThemeProvider>
  );
}

